import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import monetaryAccountService from '../services/monetaryAccountService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { formatInstantDate } from '../utils/datetime';
import MonetaryMovementsTable from '../components/MonetaryMovementsTable';
import RegisterMovementModal from '../components/RegisterMovementModal';
import '../styles/general.css';

/**
 * Read-only view of one monetary account.
 *
 * It duplicates the form's fields today. It exists anyway because it is where
 * the movements table and the running balance land next — having the route
 * and the row link in place now means that arrives as a section rather than
 * as a new page and a new link.
 */
const MonetaryAccountDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { notify } = useNotification();
  const { timezone, canManage } = useContext(AuthContext);
  const canWrite = canManage && canManage('accounting');

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMovementModal, setShowMovementModal] = useState(false);
  // Bumped after the modal saves, so the table refetches without this page
  // having to own its filters and paging.
  const [movementsReloadToken, setMovementsReloadToken] = useState(0);

  const loadAccount = useCallback(async () => {
    try {
      const response = await monetaryAccountService.getAccountById(id);
      setAccount(response.data);
    } catch (err) {
      console.error('Error loading monetary account:', err);
      notify.error(t('accounting.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (!account) {
    return <div className="page-container-wide" />;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('accounting.detailTitle')} — {account.name}</h1>
        <span className="tag-badge">
          {account.is_active ? t('common.active') : t('common.inactive')}
        </span>
      </div>

      <fieldset className="form-fieldset">
        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('accounting.accountName')}</div>
            <div className="section-value">{account.name}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('accounting.accountType')}</div>
            <div className="section-value">
              {t(`accounting.types.${account.account_type}`)}
            </div>
          </div>
          <div className="section">
            <div className="section-header">{t('accounting.location')}</div>
            <div className="section-value">{account.location}</div>
          </div>
        </div>

        <div className="grid-3">
          {/* Em dashes rather than empty cells: a cash account has neither,
              and a blank would read as missing data. Same as the list. */}
          <div className="section">
            <div className="section-header">{t('accounting.accountNumber')}</div>
            <div className="section-value">{account.account_number || '—'}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('accounting.clabe')}</div>
            <div className="section-value">{account.clabe || '—'}</div>
          </div>
        </div>

        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('accounting.createdBy')}</div>
            <div className="section-value">{account.created_by_email || '—'}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('accounting.createdAt')}</div>
            <div className="section-value">
              {formatInstantDate(account.created_at, timezone)}
            </div>
          </div>
        </div>
      </fieldset>

      <div className="page-header">
        <h2>{t('accounting.movements')}</h2>
        {/* Hidden on a deactivated account: it takes no new movements, so the
            button would only lead to a 400. */}
        {canWrite && account.is_active && (
          <button
            type="button"
            onClick={() => setShowMovementModal(true)}
            className="btn btn-primary"
          >
            {t('accounting.addMovement')}
          </button>
        )}
      </div>

      <MonetaryMovementsTable
        account={account}
        canWrite={canWrite}
        reloadToken={movementsReloadToken}
      />

      <RegisterMovementModal
        account={account}
        isOpen={showMovementModal}
        onClose={() => setShowMovementModal(false)}
        onSaved={() => {
          setShowMovementModal(false);
          setMovementsReloadToken((current) => current + 1);
        }}
      />

      <div className="form-actions">
        <button
          type="button"
          onClick={() => navigate('/monetary-accounts')}
          className="btn btn-secondary"
        >
          {t('accounting.backToList')}
        </button>
        {canWrite && (
          <button
            type="button"
            onClick={() => navigate(`/monetary-accounts/${id}/edit`)}
            className="btn btn-primary"
          >
            {t('common.edit')}
          </button>
        )}
      </div>
    </div>
  );
};

export default MonetaryAccountDetailPage;
